import React, { useState, useCallback, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileSpreadsheet, Calendar, Filter,
  TrendingUp, DollarSign, Users, MessageSquare,
  BarChart3, PieChart, Activity, Receipt, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart colors
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Export Card Component - defined outside to avoid re-renders
const ExportCard = ({ type, state, years, months, onUpdateFilter, onExport, title, description, icon: Icon, colorClass }) => {
  return (
    <Card className="border-2 hover:shadow-md transition-all" data-testid={`${type}-report-card`}>
      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
        <CardTitle className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            colorClass === 'emerald' ? 'bg-emerald-100' :
            colorClass === 'blue' ? 'bg-blue-100' :
            colorClass === 'rose' ? 'bg-rose-100' : 'bg-purple-100'
          }`}>
            <Icon className={`w-6 h-6 ${
              colorClass === 'emerald' ? 'text-emerald-600' :
              colorClass === 'blue' ? 'text-blue-600' :
              colorClass === 'rose' ? 'text-rose-600' : 'text-purple-600'
            }`} />
          </div>
          <div>
            <span className="text-lg">{title}</span>
            <p className="text-sm font-normal text-gray-500 mt-0.5">{description}</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Year
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              value={state.year}
              onChange={(e) => onUpdateFilter(type, 'year', e.target.value)}
              data-testid={`${type}-year-select`}
            >
              <option value="">All Years</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Filter className="w-4 h-4 inline mr-1" />
              Month
            </label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-100"
              value={state.month}
              onChange={(e) => onUpdateFilter(type, 'month', e.target.value)}
              disabled={!state.year}
              data-testid={`${type}-month-select`}
            >
              <option value="">All Months</option>
              {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <Button 
          onClick={onExport}
          disabled={state.loading}
          className={`w-full ${
            colorClass === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' :
            colorClass === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
            colorClass === 'rose' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-purple-600 hover:bg-purple-700'
          }`}
          data-testid={`export-${type}-btn`}
        >
          {state.loading ? (
            <>Generating...</>
          ) : (
            <>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Download CSV
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

const AdminReports = () => {
  const { adminAuth } = useOutletContext();
  
  // Analytics data
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // Recent itinerary transactions (with per-row AI cost)
  const [recentTxns, setRecentTxns] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [showAllTxns, setShowAllTxns] = useState(false);
  
  // Export states - consolidated into objects
  const [exportState, setExportState] = useState({
    transactions: { year: '', month: '', loading: false },
    gst: { year: '', month: '', loading: false },
    inquiries: { year: '', month: '', loading: false },
    leads: { year: '', month: '', loading: false }
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/reports/analytics`, {
          headers: { Authorization: `Basic ${adminAuth}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAnalytics(data);
        }
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    fetchAnalytics();
  }, [adminAuth]);

  // Fetch recent itinerary transactions
  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/instant-itinerary-leads`, {
          headers: { Authorization: `Basic ${adminAuth}` }
        });
        if (res.ok) {
          const data = await res.json();
          setRecentTxns(data.leads || []);
        }
      } catch (error) {
        console.error('Failed to fetch recent transactions:', error);
      } finally {
        setLoadingRecent(false);
      }
    };
    fetchRecent();
  }, [adminAuth]);

  // Update export filter
  const updateExportFilter = (type, field, value) => {
    setExportState(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
        // Clear month if year is cleared
        ...(field === 'year' && !value ? { month: '' } : {})
      }
    }));
  };

  // Consolidated export handler
  const exportCsv = useCallback(async (type, endpoint, filenamePrefix) => {
    const state = exportState[type];
    setExportState(prev => ({ ...prev, [type]: { ...prev[type], loading: true } }));
    
    try {
      let url = `${API_URL}${endpoint}`;
      const params = new URLSearchParams();
      if (state.year) params.append('year', state.year);
      if (state.month) params.append('month', state.month);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Basic ${adminAuth}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        const filterSuffix = state.year ? `_${state.year}${state.month ? `-${state.month}` : ''}` : '';
        a.download = `${filenamePrefix}${filterSuffix}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success(`${filenamePrefix.replace(/_/g, ' ')} report downloaded!`);
      } else {
        toast.error('Failed to download report');
      }
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setExportState(prev => ({ ...prev, [type]: { ...prev[type], loading: false } }));
    }
  }, [adminAuth, exportState]);

  // Format month label for charts
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900" data-testid="reports-title">Reports & Analytics</h1>
        <p className="text-gray-600 mt-1">Export business data and view performance insights</p>
      </div>

      {/* Summary Stats */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white" data-testid="total-revenue-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Total Revenue (incl. GST)</p>
                  <p className="text-2xl font-bold mt-1">₹{(analytics.summary.total_revenue || 0).toLocaleString()}</p>
                </div>
                <DollarSign className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white" data-testid="gst-collected-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rose-100 text-sm">GST Payable @18%</p>
                  <p className="text-2xl font-bold mt-1">₹{(analytics.summary.total_gst || 0).toLocaleString()}</p>
                </div>
                <Receipt className="w-10 h-10 text-rose-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white" data-testid="ai-cost-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">AI Generation Cost</p>
                  <p className="text-2xl font-bold mt-1">₹{(analytics.summary.total_ai_cost || 0).toLocaleString()}</p>
                </div>
                <Activity className="w-10 h-10 text-orange-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white" data-testid="net-profit-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm">Net Profit (after GST &amp; AI cost)</p>
                  <p className="text-2xl font-bold mt-1">₹{(analytics.summary.total_profit || 0).toLocaleString()}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-teal-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Inquiries</p>
                  <p className="text-2xl font-bold mt-1">{analytics.summary.total_inquiries}</p>
                </div>
                <MessageSquare className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Chat Leads</p>
                  <p className="text-2xl font-bold mt-1">{analytics.summary.total_leads}</p>
                </div>
                <Users className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Conversion Rate</p>
                  <p className="text-2xl font-bold mt-1">{analytics.summary.conversion_rate}%</p>
                </div>
                <TrendingUp className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card data-testid="revenue-chart-card">
          <CardHeader className="border-b bg-gray-50">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Revenue & Transactions (6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {loadingAnalytics ? (
              <div className="h-64 flex items-center justify-center text-gray-400">Loading...</div>
            ) : analytics?.transactions_by_month?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={analytics.transactions_by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tickFormatter={formatMonth} fontSize={12} />
                  <YAxis yAxisId="left" fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" fontSize={12} />
                  <Tooltip 
                    labelFormatter={formatMonth}
                    formatter={(value, name) => [
                      name === 'revenue' ? `₹${value.toLocaleString()}` : value,
                      name === 'revenue' ? 'Revenue' : 'Transactions'
                    ]}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="revenue" fill="#10b981" name="Revenue (₹)" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="count" fill="#3b82f6" name="Transactions" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">No transaction data available</div>
            )}
          </CardContent>
        </Card>

        {/* Inquiry Types Pie Chart */}
        <Card data-testid="inquiry-types-chart-card">
          <CardHeader className="border-b bg-gray-50">
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChart className="w-5 h-5 text-blue-600" />
              Inquiry Types Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {loadingAnalytics ? (
              <div className="h-64 flex items-center justify-center text-gray-400">Loading...</div>
            ) : analytics?.inquiries_by_type?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <RechartsPie>
                  <Pie
                    data={analytics.inquiries_by_type}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {analytics.inquiries_by_type.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Count']} />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">No inquiry data available</div>
            )}
          </CardContent>
        </Card>

        {/* Inquiries & Leads Trend */}
        <Card className="lg:col-span-2" data-testid="trends-chart-card">
          <CardHeader className="border-b bg-gray-50">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5 text-purple-600" />
              Inquiries & Leads Trend (6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {loadingAnalytics ? (
              <div className="h-64 flex items-center justify-center text-gray-400">Loading...</div>
            ) : (analytics?.inquiries_by_month?.length > 0 || analytics?.leads_by_month?.length > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={formatMonth} 
                    fontSize={12}
                    allowDuplicatedCategory={false}
                  />
                  <YAxis fontSize={12} />
                  <Tooltip labelFormatter={formatMonth} />
                  <Legend />
                  {analytics.inquiries_by_month?.length > 0 && (
                    <Line 
                      data={analytics.inquiries_by_month}
                      type="monotone" 
                      dataKey="count" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      name="Inquiries"
                      dot={{ fill: '#3b82f6' }}
                    />
                  )}
                  {analytics.leads_by_month?.length > 0 && (
                    <Line 
                      data={analytics.leads_by_month}
                      type="monotone" 
                      dataKey="count" 
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      name="Chat Leads"
                      dot={{ fill: '#8b5cf6' }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">No trend data available</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Itinerary Transactions with per-row AI Cost */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Recent Itinerary Transactions</h2>
            <p className="text-sm text-gray-500 mt-0.5">Per-itinerary AI generation cost & profit breakdown</p>
          </div>
          {recentTxns.length > 10 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllTxns(!showAllTxns)}
              data-testid="toggle-all-txns-btn"
            >
              {showAllTxns ? 'Show recent 10' : `Show all (${recentTxns.length})`}
            </Button>
          )}
        </div>
        <Card data-testid="recent-txns-card">
          <CardContent className="p-0">
            {loadingRecent ? (
              <div className="p-8 text-center text-gray-400">Loading transactions...</div>
            ) : recentTxns.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No itinerary transactions yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="recent-txns-table">
                  <thead className="bg-gray-50 border-b text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Date</th>
                      <th className="text-left px-4 py-3 font-medium">Order ID</th>
                      <th className="text-left px-4 py-3 font-medium">Customer</th>
                      <th className="text-left px-4 py-3 font-medium">Destination</th>
                      <th className="text-right px-4 py-3 font-medium">Amount Paid</th>
                      <th className="text-right px-4 py-3 font-medium">GST @18%</th>
                      <th className="text-right px-4 py-3 font-medium text-orange-600">AI Cost</th>
                      <th className="text-right px-4 py-3 font-medium text-teal-600">Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(showAllTxns ? recentTxns : recentTxns.slice(0, 10)).map((txn, idx) => {
                      const dateStr = txn.created_at ? new Date(txn.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      }) : '-';
                      const destination = txn.form?.destination || txn.itinerary?.title || '-';
                      const netAmount = txn.net_amount ?? txn.amount_paid ?? 0;
                      const cost = txn.estimated_cost ?? 0;
                      const profit = txn.profit ?? (netAmount - cost);
                      return (
                        <tr key={txn.order_id || idx} className="hover:bg-gray-50" data-testid={`txn-row-${idx}`}>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{dateStr}</td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-700">{txn.order_id || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{txn.customer_name || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{txn.customer_email || '-'}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate" title={destination}>{destination}</td>
                          <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                            ₹{(txn.amount_paid || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-4 py-3 text-right text-rose-600 whitespace-nowrap">
                            ₹{(txn.gst_amount || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-orange-600 whitespace-nowrap" data-testid={`txn-cost-${idx}`}>
                            ₹{cost.toFixed(2)}
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${profit >= 0 ? 'text-teal-600' : 'text-red-600'}`} data-testid={`txn-profit-${idx}`}>
                            ₹{profit.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t font-semibold">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-gray-700">
                        Totals ({showAllTxns ? recentTxns.length : Math.min(10, recentTxns.length)} shown)
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        ₹{(showAllTxns ? recentTxns : recentTxns.slice(0, 10))
                          .reduce((s, t) => s + (t.amount_paid || 0), 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-3 text-right text-rose-600">
                        ₹{(showAllTxns ? recentTxns : recentTxns.slice(0, 10))
                          .reduce((s, t) => s + (t.gst_amount || 0), 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-orange-600">
                        ₹{(showAllTxns ? recentTxns : recentTxns.slice(0, 10))
                          .reduce((s, t) => s + (t.estimated_cost || 0), 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-teal-600">
                        ₹{(showAllTxns ? recentTxns : recentTxns.slice(0, 10))
                          .reduce((s, t) => s + (t.profit ?? ((t.net_amount ?? t.amount_paid ?? 0) - (t.estimated_cost || 0))), 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
          <IndianRupee className="w-3 h-3" />
          AI Cost is an estimate based on tokens generated + image count. Full breakdown available in the Transactions CSV export below.
        </p>
      </div>

      {/* Export Cards */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Exports</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
          <ExportCard
            type="transactions"
            state={exportState.transactions}
            years={years}
            months={months}
            onUpdateFilter={updateExportFilter}
            onExport={() => exportCsv('transactions', '/api/admin/instant-itinerary-transactions/export', 'transactions')}
            title="Transaction Report"
            description="Instant itinerary payments with GST"
            icon={DollarSign}
            colorClass="emerald"
          />
          <ExportCard
            type="gst"
            state={exportState.gst}
            years={years}
            months={months}
            onUpdateFilter={updateExportFilter}
            onExport={() => exportCsv('gst', '/api/admin/gst-transactions/export', 'gst_transactions')}
            title="GST Report"
            description="Taxable value, CGST/SGST split for filing"
            icon={Receipt}
            colorClass="rose"
          />
          <ExportCard
            type="inquiries"
            state={exportState.inquiries}
            years={years}
            months={months}
            onUpdateFilter={updateExportFilter}
            onExport={() => exportCsv('inquiries', '/api/admin/inquiries/export', 'inquiries')}
            title="Inquiry Report"
            description="Customer inquiries and visa apps"
            icon={MessageSquare}
            colorClass="blue"
          />
          <ExportCard
            type="leads"
            state={exportState.leads}
            years={years}
            months={months}
            onUpdateFilter={updateExportFilter}
            onExport={() => exportCsv('leads', '/api/admin/chat-leads/export', 'chat_leads')}
            title="Chat Leads Report"
            description="AI chatbot lead captures"
            icon={Users}
            colorClass="purple"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
